import React from 'react';
import styled, { css } from 'styled-components';
import { partial } from 'lodash';
import { Nav } from 'reactstrap';
import TabItem from './TabItem';
import { TAB_BORDER_WIDTH, TAB_RAIL_COLOR } from './constants';

interface IWrapper {
  isSortable: boolean;
  $redesign?: boolean;
}

const sortableStyles = css`
  .sortable.nav {
    flex-wrap: nowrap;
  }
`;
const Wrapper = styled.div<IWrapper>`
  ${(props) => props.isSortable && sortableStyles};
  // Grow to fill the row so the tab rail spans the full width when this is the
  // only child; when a toolbar sits alongside it, the rail stops at the toolbar.
  // min-width: 0 lets it shrink below the tabs' width so they scroll instead of
  // being clipped by the page.
  ${(props) => props.$redesign && 'flex: 1; min-width: 0;'}
`;

const legacySmallScreenStyles = css`
  @media all and (max-width: 550px) {
    flex-wrap: nowrap;
    max-width: 420px;
    overflow-x: auto;
    overflow-y: hidden;
  }
  @media all and (max-width: 480px) {
    max-width: 350px;
  }
  @media all and (max-width: 400px) {
    max-width: 270px;
  }
`;

// The rail is an inset shadow over the bottom padding rather than a border: a
// scroll container clips its border area, which would hide the active tab's
// underline where it is pulled down over the rail.
const redesignStyles = css`
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: ${TAB_BORDER_WIDTH}px;
  box-shadow: inset 0 -${TAB_BORDER_WIDTH}px 0 ${TAB_RAIL_COLOR};
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const NavTabsStyled = styled(Nav)`
  border: none;
  display: flex;
  ${(props) => (props.$redesign ? redesignStyles : legacySmallScreenStyles)}
  ${(props) => props.$newDesign && 'border-bottom: 3px solid #F2F2F2;'}
`;

export interface TabListItem {
  id: string;
  label: string | (() => string);
  isHidden?: boolean;
  warning?: boolean;
}

interface ITabsHeaderProps {
  tabsList: TabListItem[];
  activeTab: string;
  onTabClick: (tabName: string) => void;
  isSortable?: boolean;
  newDesign?: boolean;
  /**
   * The `newDesign` feature-flag styling (45px bar, 19px labels). Kept separate
   * from `newDesign` so ShopPaymentSettings, which opts into that older variant
   * unconditionally, is unaffected by the flag.
   */
  redesign?: boolean;
}

export const TabsHeader = ({
  tabsList,
  onTabClick,
  activeTab,
  isSortable = false,
  newDesign = false,
  redesign = false
}: ITabsHeaderProps) => {
  const isTabActive = (tabId: string) => activeTab === tabId;

  return (
    <Wrapper isSortable={isSortable} $redesign={redesign}>
      <NavTabsStyled tabs $newDesign={newDesign} $redesign={redesign}>
        {tabsList.map((name) =>
          !name.isHidden ? (
            <TabItem
              name={typeof name.label === 'function' ? name.label() : name.label}
              key={name.id}
              onClick={partial(onTabClick, name.id)}
              isActive={isTabActive(name.id)}
              className={`pt-${name.id}`}
              data-cy={`tab-${name.id}`}
              newDesign={newDesign}
              redesign={redesign}
              warning={name.warning}
            />
          ) : null
        )}
      </NavTabsStyled>
    </Wrapper>
  );
};
